import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useHubSocket } from "@/hooks/useHubSocket";
import { Card, CardContent } from "@/components/ui/card";
import LiveShareBar from "@/components/landing/LiveShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { SectionLoader, EmptyState } from "@/components/common";
import { SessionReplayToggle } from "@/components/hub/HomepageReplayControls";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { initials } from "@/lib/format";
import { externalUrl } from "@/lib/url";
import {
  ArrowLeft, Users, Wifi, WifiOff, ChevronUp, MessageSquare, Sparkles, Send,
  CheckCircle2, Star, Trash2, BarChart3, Plus, Radio, Square, X, UserCheck, Loader2, Pin, AudioLines, SlidersHorizontal,
  Video, VideoOff, MoreVertical, Clock, Ban, ShieldOff, Copy, Eye, EyeOff, Link2, Unlink, MonitorPlay, ChevronDown, RotateCw,
  ExternalLink, Megaphone,
} from "lucide-react";

const REACTIONS = ["\u{1F44F}", "\u2764\uFE0F", "\u{1F525}", "\u{1F4A1}", "\u{1F602}", "\u{1F389}"];
const TIMEOUT_PRESETS = [
  { minutes: 5, label: "5 minutes" },
  { minutes: 15, label: "15 minutes" },
  { minutes: 60, label: "1 hour" },
];

function LiveDot() {
  return (
    <span className="relative flex h-2.5 w-2.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
    </span>
  );
}

// ---------------- Host control-room status (real data only) ----------------
function StatTile({ icon: Icon, label, testId, children }) {
  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}{label}
      </div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ControlRoomStatus({ status, connected, live, viewerCount, checkinCount, openQuestions }) {
  const showBadge = status === "live"
    ? <Badge className="border border-red-200 bg-red-50 text-red-700" data-testid="control-status-show-badge"><LiveDot /><span className="ml-1.5">On air</span></Badge>
    : status === "ended"
      ? <Badge variant="outline" className="text-muted-foreground" data-testid="control-status-show-badge">Ended</Badge>
      : <Badge variant="secondary" data-testid="control-status-show-badge">Standby</Badge>;
  const connBadge = connected
    ? <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-800" data-testid="control-status-connection-badge"><Wifi className="mr-1 h-3 w-3" />Connected</Badge>
    : <Badge className="border border-amber-200 bg-amber-50 text-amber-800" data-testid="control-status-connection-badge"><WifiOff className="mr-1 h-3 w-3" />Reconnecting</Badge>;
  const videoBadge = !live
    ? <Badge variant="outline" className="text-muted-foreground" data-testid="control-status-video-badge">Not linked</Badge>
    : live.state === "live"
      ? <Badge className="border border-red-200 bg-red-50 text-red-700" data-testid="control-status-video-badge"><LiveDot /><span className="ml-1.5">On air</span></Badge>
      : <Badge variant="secondary" data-testid="control-status-video-badge">Standby</Badge>;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="control-room-status">
      <StatTile icon={Radio} label="Show" testId="control-status-show">{showBadge}</StatTile>
      <StatTile icon={Wifi} label="Connection" testId="control-status-connection">{connBadge}</StatTile>
      <StatTile icon={Video} label="Live video" testId="control-status-video">{videoBadge}</StatTile>
      <StatTile icon={Users} label="Audience" testId="control-status-audience">
        <div className="flex items-baseline gap-1.5">
          <span className="text-lg font-semibold tabular-nums text-foreground" data-testid="control-status-watching">{viewerCount}</span>
          <span className="text-xs text-muted-foreground">watching</span>
        </div>
        <p className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">{checkinCount} checked in · {openQuestions} open Q</p>
      </StatTile>
    </div>
  );
}

// ---------------- Live video player ----------------
// Clickable companion banner overlaid on the live player. Mirrors the producer's
// broadcast graphics: a clickable Sponsor CTA and the current on-air audience
// question (Spotlight). Placed at the top so it never covers the player controls.
function PlayerCompanionBanner({ sponsor, spotlight }) {
  const hasSponsor = !!(sponsor && (sponsor.title || sponsor.url));
  const hasQA = !!(spotlight && spotlight.text);
  if (!hasSponsor && !hasQA) return null;

  const scrollToQuestion = () => {
    try {
      document.querySelector('[data-testid="hub-spotlight-banner"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (e) { console.debug("spotlight scroll skipped", e); }
  };

  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex flex-col items-start gap-1.5 p-3" data-testid="hub-companion-banner">
      {hasQA && (
        <button
          type="button"
          onClick={scrollToQuestion}
          className="pointer-events-auto flex max-w-[92%] items-center gap-2 rounded-lg border border-primary/40 bg-[hsl(var(--card))]/85 px-3 py-1.5 text-left shadow-lg backdrop-blur-sm transition-colors hover:border-primary"
          data-testid="hub-companion-qa"
        >
          <Megaphone className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">On air</span>
          <span className="truncate text-xs text-foreground">{spotlight.text}</span>
        </button>
      )}
      {hasSponsor && (
        sponsor.url ? (
          <a
            href={externalUrl(sponsor.url)}
            target="_blank"
            rel="noreferrer"
            className="pointer-events-auto flex max-w-[92%] items-center gap-2 rounded-lg border border-primary/40 bg-[hsl(var(--card))]/85 px-3 py-1.5 shadow-lg backdrop-blur-sm transition-colors hover:border-primary"
            data-testid="hub-companion-sponsor"
          >
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--primary))]">{sponsor.eyebrow || "Sponsored"}</span>
            <span className="truncate text-xs font-medium text-foreground">{sponsor.title || sponsor.url}</span>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary px-1.5 py-0.5 text-[11px] font-semibold text-primary-foreground">
              Visit <ExternalLink className="h-3 w-3" />
            </span>
          </a>
        ) : (
          <div
            className="pointer-events-auto flex max-w-[92%] items-center gap-2 rounded-lg border border-primary/40 bg-[hsl(var(--card))]/85 px-3 py-1.5 shadow-lg backdrop-blur-sm"
            data-testid="hub-companion-sponsor"
          >
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--primary))]">{sponsor.eyebrow || "Sponsored"}</span>
            <span className="truncate text-xs font-medium text-foreground">{sponsor.title}</span>
          </div>
        )
      )}
    </div>
  );
}

// Older Apple mobile devices (especially older iPads) can render a black screen on the
// Cloudflare Stream player unless the embed autoplays muted + inline. Detect Apple mobile
// and request muted autoplay there ONLY; desktop/Android keep click-to-play with sound.
const IS_APPLE_MOBILE = (() => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOSDesktop = navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  return iOS || iPadOSDesktop;
})();

function buildLivePlayerSrc(iframeUrl) {
  if (!IS_APPLE_MOBILE || !iframeUrl) return iframeUrl;
  try {
    const u = new URL(iframeUrl);
    if (!u.searchParams.has("autoplay")) u.searchParams.set("autoplay", "true");
    if (!u.searchParams.has("muted")) u.searchParams.set("muted", "true");
    if (!u.searchParams.has("preload")) u.searchParams.set("preload", "auto");
    return u.toString();
  } catch (e) {
    return iframeUrl;
  }
}

function LivePlayer({ live, nonce = 0, onReload, sponsor, spotlight, shareTitle }) {
  const isLive = live.state === "live";
  const playerSrc = buildLivePlayerSrc(live.iframe);
  return (
    <Card className="overflow-hidden border-border bg-card" data-testid="hub-live-player">
      <div className="relative aspect-video w-full bg-black">
        <iframe
          key={`${playerSrc}::${nonce}`}
          src={playerSrc}
          title={live.name || "Live stream"}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
          data-testid="hub-live-iframe"
        />
        <PlayerCompanionBanner sponsor={sponsor} spotlight={spotlight} />
        {!isLive && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center bg-gradient-to-t from-black/70 to-transparent p-3">
            <span className="text-xs font-medium text-white/90" data-testid="hub-live-waiting">
              Waiting for the hosts to go on camera…
            </span>
          </div>
        )}
      </div>
      <CardContent className="flex items-center justify-between gap-2 p-3">
        <div className="flex min-w-0 items-center gap-2">
          <Video className="h-4 w-4 shrink-0 text-primary" />
          <span className="truncate text-sm font-medium text-foreground">{live.name || "Live broadcast"}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {onReload && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" onClick={onReload} data-testid="hub-live-reload" title="Stuck on a spinner? Reload the video">
              <RotateCw className="mr-1.5 h-3.5 w-3.5" />Reload video
            </Button>
          )}
          {isLive
            ? <Badge className="border border-red-200 bg-red-50 text-red-700"><LiveDot /><span className="ml-1.5">On air</span></Badge>
            : <Badge variant="outline" className="text-muted-foreground">Standby</Badge>}
        </div>
      </CardContent>
      {isLive && <LiveShareBar title={shareTitle || live.name} className="border-t border-border px-3 py-2" testPrefix="hub-live-share" />}
    </Card>
  );
}

function VideoPlaceholder({ isCore, onLink }) {
  return (
    <Card className="border-dashed border-border bg-card" data-testid="hub-video-placeholder">
      <CardContent className="flex aspect-video flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-muted-foreground">
          <VideoOff className="h-6 w-6" />
        </div>
        {isCore ? (
          <>
            <p className="text-sm font-medium text-foreground">No live video yet</p>
            <p className="max-w-sm text-sm text-muted-foreground">Go on camera, or link an input for OBS. Members see the player here the moment you go live.</p>
            <Button size="sm" onClick={onLink} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="hub-placeholder-link-button"><Video className="mr-1.5 h-4 w-4" />Link live video</Button>
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-foreground">The video stream hasn't started yet</p>
            <p className="max-w-sm text-sm text-muted-foreground">Hang out in the chat — the live video will appear here as soon as the hosts go on camera.</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------- Live chat ----------------
function ChatMessage({ m, isCore, onDelete, onTimeout, onUntimeout }) {
  const canModerate = isCore;
  return (
    <div className="group flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-secondary/60" data-testid="hub-chat-message">
      <Avatar className="mt-0.5 h-6 w-6 shrink-0"><AvatarFallback className="bg-secondary text-[10px] text-foreground">{initials(m.author_name)}</AvatarFallback></Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug">
          <span className={`font-semibold ${m.is_mine ? "text-primary" : "text-foreground"}`}>{m.author_name}{m.is_mine ? " (you)" : ""}</span>
          <span className="text-muted-foreground">: </span>
          <span className="break-words text-foreground">{m.text}</span>
        </p>
      </div>
      {canModerate && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100" data-testid="hub-chat-mod-trigger" aria-label="Moderate message">
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="text-xs">Moderate</DropdownMenuLabel>
            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(m)} data-testid="hub-chat-delete">
              <Trash2 className="mr-2 h-3.5 w-3.5" />Delete message
            </DropdownMenuItem>
            {!m.is_mine && m.user_id && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid="hub-chat-timeout-trigger"><Clock className="mr-2 h-3.5 w-3.5" />Time out {m.author_name?.split(" ")[0] || "member"}</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {TIMEOUT_PRESETS.map((p) => (
                      <DropdownMenuItem key={p.minutes} onClick={() => onTimeout(m, p.minutes)} data-testid={`hub-chat-timeout-${p.minutes}`}>
                        <Ban className="mr-2 h-3.5 w-3.5" />{p.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuItem onClick={() => onUntimeout(m)} data-testid="hub-chat-untimeout">
                  <ShieldOff className="mr-2 h-3.5 w-3.5" />Remove time-out
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function timeoutRemainingLabel(until) {
  if (!until) return null;
  const ms = new Date(until).getTime() - Date.now();
  if (ms <= 0) return null;
  const mins = Math.ceil(ms / 60000);
  return mins <= 1 ? "less than a minute" : `${mins} minutes`;
}

function LiveChat({ chat, isCore, connected, timedOutUntil, dailyCap, onSend, onDelete, onTimeout, onUntimeout }) {
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.length]);

  const isTimedOut = !!timeoutRemainingLabel(timedOutUntil);

  const submit = () => {
    if (!text.trim()) return;
    if (onSend(text)) setText("");
  };

  return (
    <Card className="flex h-[440px] flex-col border-border bg-card lg:h-[560px]" data-testid="hub-chat-panel">
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">Live chat</span>
        </div>
        <span className="flex items-center gap-1 text-xs text-muted-foreground" title={connected ? "Connected" : "Reconnecting"}>
          {connected ? <Wifi className="h-3.5 w-3.5 text-primary" /> : <WifiOff className="h-3.5 w-3.5 text-amber-500" />}
        </span>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-0.5 overflow-y-auto px-2 py-2" data-testid="hub-chat-scroll">
        {chat.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-muted-foreground">
            <Sparkles className="h-6 w-6" />
            <p className="text-sm">No messages yet. Say hello!</p>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {chat.map((m) => (
              <motion.div key={m.message_id} layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                <ChatMessage m={m} isCore={isCore} onDelete={onDelete} onTimeout={onTimeout} onUntimeout={onUntimeout} />
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      <div className="border-t border-border p-2.5">
        {isTimedOut ? (
          <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50/70 px-3 py-2 text-sm text-amber-800" data-testid="hub-chat-timed-out">
            <Ban className="h-4 w-4 shrink-0" />
            <span>You're timed out — {timeoutRemainingLabel(timedOutUntil)} left.</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
              placeholder="Send a message"
              maxLength={500}
              disabled={!connected}
              data-testid="hub-chat-input"
            />
            <Button size="icon" onClick={submit} disabled={!connected || !text.trim()} className="shrink-0 bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="hub-chat-send" aria-label="Send message">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        )}
        {dailyCap ? <p className="mt-1.5 px-0.5 text-[11px] text-muted-foreground">Earn +1 point per message (up to {dailyCap}/day).</p> : null}
      </div>
    </Card>
  );
}

function QuestionCard({ q, isCore, isSpotlight, onUpvote, onSpotlight, onAnswer, onDismiss, onDelete }) {
  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
      <Card className={`border-border bg-card ${q.status === "answered" ? "opacity-80" : ""} ${isSpotlight ? "ring-2 ring-primary" : ""}`} data-testid="hub-question-card">
        <CardContent className="flex gap-3 p-4">
          <button
            onClick={() => onUpvote(q)}
            className={`flex h-16 w-14 shrink-0 flex-col items-center justify-center rounded-xl border transition-colors ${q.has_upvoted ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary text-foreground hover:border-primary/40"}`}
            data-testid="hub-upvote-button"
          >
            <ChevronUp className="h-5 w-5" />
            <span className="text-sm font-semibold tabular-nums">{q.upvote_count}</span>
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6"><AvatarFallback className="bg-secondary text-[10px] text-foreground">{q.is_anonymous ? "?" : initials(q.author_name)}</AvatarFallback></Avatar>
              <span className="text-sm font-medium text-foreground">{q.author_name}{q.is_mine ? " (you)" : ""}</span>
              {q.status === "answered" && <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-800"><CheckCircle2 className="mr-1 h-3 w-3" />Answered</Badge>}
            </div>
            <p className="mt-1.5 whitespace-pre-line text-[15px] leading-6 text-foreground">{q.text}</p>
            <div className="mt-2 flex flex-wrap items-center gap-1">
              {isCore && (
                <>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onSpotlight(q)} data-testid="hub-spotlight-button"><Star className={`mr-1 h-3.5 w-3.5 ${isSpotlight ? "fill-amber-400 text-amber-500" : ""}`} />{isSpotlight ? "Unspotlight" : "Spotlight"}</Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onAnswer(q)}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />{q.status === "answered" ? "Reopen" : "Mark answered"}</Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-destructive" onClick={() => onDismiss(q)} data-testid="hub-dismiss-button"><X className="mr-1 h-3.5 w-3.5" />Dismiss</Button>
                </>
              )}
              {!isCore && q.is_mine && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-destructive" onClick={() => onDelete(q)}><Trash2 className="mr-1 h-3.5 w-3.5" />Delete</Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function PollCard({ poll, isCore, onVote, onClose, onDelete }) {
  const total = poll.total_votes || 0;
  return (
    <Card className="border-border bg-card" data-testid="hub-poll-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="font-medium text-foreground">{poll.question}</p>
          {poll.status === "closed" ? <Badge variant="outline">Closed</Badge> : <Badge variant="secondary">Live</Badge>}
        </div>
        <div className="mt-3 space-y-2">
          {poll.options.map((o) => {
            const pct = total > 0 ? Math.round((o.count / total) * 100) : 0;
            const mine = poll.my_vote === o.id;
            return (
              <button key={o.id} disabled={poll.status === "closed"} onClick={() => onVote(poll, o)} className="relative w-full overflow-hidden rounded-lg border border-border p-2.5 text-left disabled:cursor-default" data-testid="hub-poll-option">
                <div className={`absolute inset-y-0 left-0 ${mine ? "bg-primary/20" : "bg-secondary"}`} style={{ width: `${pct}%` }} />
                <div className="relative flex items-center justify-between">
                  <span className={`text-sm ${mine ? "font-semibold text-primary" : "text-foreground"}`}>{o.text}{mine ? " \u2713" : ""}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">{pct}% · {o.count}</span>
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-muted-foreground tabular-nums">{total} vote{total === 1 ? "" : "s"}</span>
          {isCore && (
            <div className="flex gap-1">
              {poll.status !== "closed" && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onClose(poll)}>Close</Button>}
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-destructive" onClick={() => onDelete(poll)}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function PodcastHub() {
  const { sessionId } = useParams();
  const { user, can } = useAuth();
  const isCore = can("producer_tools");  // can host/moderate the hub
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState("scheduled");
  const [questions, setQuestions] = useState([]);
  const [polls, setPolls] = useState([]);
  const [spotlight, setSpotlight] = useState(null);
  const [sponsorBanner, setSponsorBanner] = useState(null);
  const [viewerCount, setViewerCount] = useState(1);
  const [checkinCount, setCheckinCount] = useState(0);
  const [myCheckedIn, setMyCheckedIn] = useState(false);
  const [reactions, setReactions] = useState([]);
  const [askText, setAskText] = useState("");
  const [anon, setAnon] = useState(false);
  const [pollOpen, setPollOpen] = useState(false);
  const [checkinOpen, setCheckinOpen] = useState(false);
  // Phase 14 — live video + chat
  const [live, setLive] = useState(null);
  const [chat, setChat] = useState([]);
  const [myTimeoutUntil, setMyTimeoutUntil] = useState(null);
  const [dailyCaps, setDailyCaps] = useState({ hub_chat: 10, hub_reaction: 10 });
  const [linkOpen, setLinkOpen] = useState(false);
  const [playerNonce, setPlayerNonce] = useState(0);
  const [pointsFlash, setPointsFlash] = useState(null);
  const [homepageReplayEnabled, setHomepageReplayEnabled] = useState(null);
  const reactionId = useRef(0);
  const flashRef = useRef(null);

  const myId = user?.user_id;

  const reloadPlayer = useCallback(() => setPlayerNonce((n) => n + 1), []);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/hub/sessions/${sessionId}`);
      const d = r.data;
      setSession(d.session);
      setStatus(d.session.status);
      setQuestions(d.questions);
      setPolls(d.polls);
      setSpotlight(d.spotlight);
      setSponsorBanner(d.session?.overlay_sponsor || null);
      setViewerCount(d.viewer_count || 1);
      setCheckinCount(d.checkin_count || 0);
      setMyCheckedIn(d.my_checked_in);
      setLive(d.live || null);
      setChat(d.chat || []);
      setMyTimeoutUntil(d.my_timeout_until || null);
      setHomepageReplayEnabled(d.homepage_replay_enabled ?? null);
      if (d.daily_caps) setDailyCaps(d.daily_caps);
    } catch (e) {
      toast.error("Session not found");
      navigate("/portal/hub");
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Auto-clear my time-out once it expires.
  useEffect(() => {
    if (!myTimeoutUntil) return;
    const ms = new Date(myTimeoutUntil).getTime() - Date.now();
    if (ms <= 0) { setMyTimeoutUntil(null); return; }
    const t = setTimeout(() => setMyTimeoutUntil(null), ms + 250);
    return () => clearTimeout(t);
  }, [myTimeoutUntil]);

  const pushReaction = useCallback((emoji) => {
    const id = ++reactionId.current;
    const left = 60 + Math.random() * 30; // percent
    setReactions((prev) => [...prev, { id, emoji, left }]);
    setTimeout(() => setReactions((prev) => prev.filter((r) => r.id !== id)), 2400);
  }, []);

  const flashPoints = useCallback((pts) => {
    const id = Date.now();
    setPointsFlash({ id, pts });
    if (flashRef.current) clearTimeout(flashRef.current);
    flashRef.current = setTimeout(() => setPointsFlash(null), 1500);
  }, []);

  const onMessage = useCallback((msg) => {
    switch (msg.type) {
      case "presence": setViewerCount(msg.count); break;
      case "question_new":
        setQuestions((prev) => prev.some((q) => q.question_id === msg.question.question_id)
          ? prev
          : [...prev, { ...msg.question, is_mine: msg.question.author_user_id === myId, has_upvoted: false }]);
        break;
      case "question_updated":
        setQuestions((prev) => prev.map((q) => q.question_id === msg.question_id ? { ...q, upvote_count: msg.upvote_count, status: msg.status } : q));
        break;
      case "question_removed":
        setQuestions((prev) => prev.filter((q) => q.question_id !== msg.question_id));
        setSpotlight((sp) => (sp && sp.question_id === msg.question_id ? null : sp));
        break;
      case "spotlight": setSpotlight(msg.question); break;
      case "overlay_update": setSponsorBanner(msg.sponsor || null); break;
      case "poll_new":
        setPolls((prev) => prev.some((p) => p.poll_id === msg.poll.poll_id) ? prev : [msg.poll, ...prev]);
        break;
      case "poll_updated":
        setPolls((prev) => prev.map((p) => p.poll_id === msg.poll.poll_id ? { ...msg.poll, my_vote: p.my_vote ?? msg.poll.my_vote } : p));
        break;
      case "poll_removed":
        setPolls((prev) => prev.filter((p) => p.poll_id !== msg.poll_id));
        break;
      case "checkin": setCheckinCount(msg.count); break;
      case "session_status": setStatus(msg.status); break;
      case "reaction": pushReaction(msg.emoji); break;
      // ----- Phase 14 -----
      case "chat_new":
        setChat((prev) => prev.some((m) => m.message_id === msg.message.message_id)
          ? prev
          : [...prev, { ...msg.message, is_mine: msg.message.user_id === myId }]);
        break;
      case "chat_removed":
        setChat((prev) => prev.filter((m) => m.message_id !== msg.message_id));
        break;
      case "user_timeout":
        if (msg.user_id === myId) { setMyTimeoutUntil(msg.until); toast.warning(`You've been timed out for ${msg.minutes} min`); }
        break;
      case "user_untimeout":
        if (msg.user_id === myId) { setMyTimeoutUntil(null); toast.success("Your time-out was lifted"); }
        break;
      case "chat_rejected":
        if (msg.reason === "timeout") { setMyTimeoutUntil(msg.until); toast.error("You're timed out \u2014 you can't chat right now"); }
        break;
      case "live_linked":
        // The WS broadcast carries the PUBLIC payload (no host-only secrets like
        // webrtc_url/rtmps). If we (the host) already hold the secret payload for
        // this same input, merge so we don't lose the WHIP ingest URL.
        setLive((prev) => (prev && prev.live_id === msg.live?.live_id ? { ...prev, ...msg.live } : msg.live));
        break;
      case "live_unlinked": setLive(null); break;
      case "live_state":
        // The broadcast's true on-air state changed — update the badge and, when
        // it goes live, reload the player so viewers stuck on a spinner recover.
        setLive((prev) => (prev ? { ...prev, state: msg.state } : prev));
        if (msg.state === "live") setPlayerNonce((n) => n + 1);
        break;
      case "points_awarded": flashPoints(msg.points || 1); break;
      default: break;
    }
  }, [myId, pushReaction, flashPoints]);

  const { connected, sendReaction, sendChat, deleteChatMessage, timeoutUser, untimeoutUser } = useHubSocket(sessionId, onMessage);

  const sortedQuestions = [...questions].sort((a, b) => (b.upvote_count - a.upvote_count) || (a.created_at || "").localeCompare(b.created_at || ""));
  const openQuestions = questions.filter((q) => q.status !== "answered").length;
  const effectiveLive = live;

  // ----- Actions -----
  const submitAsk = async () => {
    if (!askText.trim()) return;
    try {
      const r = await api.post(`/hub/sessions/${sessionId}/questions`, { text: askText.trim(), is_anonymous: anon });
      setQuestions((prev) => prev.some((q) => q.question_id === r.data.question_id) ? prev : [...prev, r.data]);
      setAskText("");
      toast.success("Question posted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post question"); }
  };

  const toggleUpvote = async (q) => {
    setQuestions((prev) => prev.map((x) => x.question_id === q.question_id ? { ...x, has_upvoted: !x.has_upvoted, upvote_count: x.upvote_count + (x.has_upvoted ? -1 : 1) } : x));
    try {
      const r = await api.post(`/hub/questions/${q.question_id}/upvote`);
      setQuestions((prev) => prev.map((x) => x.question_id === q.question_id ? { ...x, has_upvoted: r.data.has_upvoted, upvote_count: r.data.upvote_count } : x));
    } catch (e) { load(); }
  };

  const doSpotlight = async (q) => {
    const isCurrent = spotlight && spotlight.question_id === q.question_id;
    try { await api.post(`/hub/sessions/${sessionId}/spotlight`, { question_id: isCurrent ? null : q.question_id }); }
    catch (e) { toast.error("Failed"); }
  };
  const doAnswer = async (q) => {
    try { await api.post(`/hub/questions/${q.question_id}/status`, { status: q.status === "answered" ? "open" : "answered" }); }
    catch (e) { toast.error("Failed"); }
  };
  const doDismiss = async (q) => {
    try { await api.post(`/hub/questions/${q.question_id}/status`, { status: "dismissed" }); }
    catch (e) { toast.error("Failed"); }
  };
  const doDelete = async (q) => {
    try { await api.delete(`/hub/questions/${q.question_id}`); }
    catch (e) { toast.error("Failed"); }
  };

  const votePoll = async (poll, opt) => {
    setPolls((prev) => prev.map((p) => p.poll_id === poll.poll_id ? { ...p, my_vote: opt.id } : p));
    try {
      const r = await api.post(`/hub/polls/${poll.poll_id}/vote`, { option_id: opt.id });
      setPolls((prev) => prev.map((p) => p.poll_id === poll.poll_id ? r.data : p));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not vote"); load(); }
  };
  const closePoll = async (poll) => { try { await api.post(`/hub/polls/${poll.poll_id}/close`); } catch (e) { toast.error("Failed"); } };
  const deletePoll = async (poll) => { try { await api.delete(`/hub/polls/${poll.poll_id}`); } catch (e) { toast.error("Failed"); } };

  const setSessionStatus = async (s) => {
    try { await api.post(`/hub/sessions/${sessionId}/status`, { status: s }); setStatus(s); toast.success(s === "live" ? "You're live!" : s === "ended" ? "Session ended" : "Updated"); }
    catch (e) { toast.error("Failed"); }
  };

  const setReplayOnHomepage = async (visible) => {
    try {
      await api.put(`/hub/sessions/${sessionId}/homepage-replay`, { visible });
      setSession((prev) => (prev ? { ...prev, replay_on_homepage: visible } : prev));
      toast.success(visible ? "Replay is back on the homepage" : "Replay hidden from the homepage");
    } catch (e) { toast.error("Could not update"); }
  };

  // Legacy sessions (not backed by a Studio show): announce live + optional OBS/RTMPS input.
  const goLiveAnnounce = useCallback(async () => {
    try { await api.post(`/hub/sessions/${sessionId}/status`, { status: "live" }); setStatus("live"); toast.success("You're live!"); }
    catch (e) { toast.error("Could not announce the session is live"); }
  }, [sessionId]);

  const selfCheckin = async () => {
    try { await api.post(`/hub/sessions/${sessionId}/checkin`); setMyCheckedIn(true); toast.success("You're checked in!"); }
    catch (e) { toast.error("Failed"); }
  };

  const react = (emoji) => sendReaction(emoji);

  // Chat moderation handlers
  const onChatSend = (text) => sendChat(text);
  const onChatDelete = (m) => deleteChatMessage(m.message_id);
  const onChatTimeout = (m, minutes) => { timeoutUser(m.user_id, minutes); toast.success(`${m.author_name?.split(" ")[0] || "Member"} timed out for ${minutes} min`); };
  const onChatUntimeout = (m) => { untimeoutUser(m.user_id); toast.success("Time-out removed"); };

  if (loading) return <SectionLoader label="Joining the hub\u2026" />;

  return (
    <div className="relative space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <Link to="/portal/hub" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All sessions</Link>
          <div className="mt-1 flex items-center gap-2">
            <h1 className="truncate font-serif text-2xl font-semibold text-foreground">{session?.title}</h1>
            {status === "live" ? <Badge className="border border-red-200 bg-red-50 text-red-700"><LiveDot /><span className="ml-1.5">LIVE</span></Badge>
              : status === "ended" ? <Badge variant="outline">Ended</Badge>
              : <Badge variant="secondary">Scheduled</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5 tabular-nums" data-testid="hub-viewer-count"><Users className="h-4 w-4" />{viewerCount} watching</span>
          <span className="flex items-center gap-1.5" title={connected ? "Live connection" : "Reconnecting"}>{connected ? <Wifi className="h-4 w-4 text-primary" /> : <WifiOff className="h-4 w-4 text-amber-500" />}</span>
        </div>
      </div>

      {/* Host control room */}
      {isCore && (
        <Card className="border-primary/30 bg-card" data-testid="control-room">
          <CardContent className="space-y-3 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">Control room</span>
              <span className="text-[11px] text-muted-foreground">{status === "live" ? "You're live" : status === "ended" ? "This session has ended" : session?.show_id ? "Run the show from the producer console" : "Go live when you're ready"}</span>
            </div>
            <ControlRoomStatus status={status} connected={connected} live={effectiveLive} viewerCount={viewerCount} checkinCount={checkinCount} openQuestions={openQuestions} />
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
              {session?.show_id ? (
                <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="hub-open-console">
                  <Link to={`/studio/shows/${session.show_id}`}><MonitorPlay className="mr-1.5 h-4 w-4" />Open producer console</Link>
                </Button>
              ) : (
                <>
                  {status !== "live" && status !== "ended" && (
                    <Button size="sm" onClick={goLiveAnnounce} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="hub-golive-button"><Radio className="mr-1.5 h-4 w-4" />Go live</Button>
                  )}
                  <Button size="sm" variant="secondary" onClick={() => setLinkOpen(true)} data-testid="hub-livevideo-button">
                    <Video className="mr-1.5 h-4 w-4" />{live ? "Live video" : "Link OBS / RTMPS video"}
                  </Button>
                </>
              )}
              {status === "live" && !session?.show_id && (
                <Button size="sm" variant="outline" onClick={() => setSessionStatus("ended")} data-testid="hub-end-button"><Square className="mr-1.5 h-4 w-4" />End show</Button>
              )}
              <Button size="sm" variant="secondary" onClick={() => setPollOpen(true)} data-testid="hub-newpoll-button"><BarChart3 className="mr-1.5 h-4 w-4" />New poll</Button>
              <Button size="sm" variant="secondary" onClick={() => setCheckinOpen(true)} data-testid="hub-checkin-button"><UserCheck className="mr-1.5 h-4 w-4" />Check-in ({checkinCount})</Button>
            </div>
            {status === "ended" && (
              <SessionReplayToggle visible={session?.replay_on_homepage !== false} globalEnabled={homepageReplayEnabled} onChange={setReplayOnHomepage} testId="hub-replay-homepage-toggle" />
            )}
          </CardContent>
        </Card>
      )}

      {/* Member check-in prompt */}
      {!isCore && !myCheckedIn && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex items-center justify-between p-3">
            <span className="text-sm text-foreground">Here in person? Check in so the hosts know you're present.</span>
            <Button size="sm" onClick={selfCheckin} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="hub-self-checkin"><UserCheck className="mr-1.5 h-4 w-4" />Check in</Button>
          </CardContent>
        </Card>
      )}

      {/* Watch + chat (Twitch-style) */}
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-2">
          {effectiveLive ? <LivePlayer live={effectiveLive} nonce={playerNonce} onReload={reloadPlayer} sponsor={sponsorBanner} spotlight={spotlight} shareTitle={session?.title} /> : <VideoPlaceholder isCore={isCore} onLink={() => setLinkOpen(true)} />}
        </div>
        <div className="lg:col-span-1">
          <LiveChat
            chat={chat} isCore={isCore} connected={connected}
            timedOutUntil={myTimeoutUntil} dailyCap={dailyCaps?.hub_chat}
            onSend={onChatSend} onDelete={onChatDelete} onTimeout={onChatTimeout} onUntimeout={onChatUntimeout}
          />
        </div>
      </div>

      {/* Spotlight */}
      <AnimatePresence>
        {spotlight && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Card className="border-amber-300 bg-amber-50/70" data-testid="hub-spotlight-banner">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-amber-700"><Pin className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">Spotlight question</span></div>
                <p className="mt-2 font-serif text-xl text-foreground">{spotlight.text}</p>
                <p className="mt-1 text-sm text-amber-800">{spotlight.author_name} · {spotlight.upvote_count} upvotes</p>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Questions */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <Label className="text-sm font-semibold">Ask the hosts a question</Label>
              <Textarea className="mt-2" rows={2} value={askText} onChange={(e) => setAskText(e.target.value)} placeholder="What's on your mind?" data-testid="hub-ask-input" />
              <div className="mt-2 flex items-center justify-between">
                {session?.allow_anonymous ? (
                  <label className="flex items-center gap-2 text-sm text-muted-foreground"><Switch checked={anon} onCheckedChange={setAnon} data-testid="hub-anon-toggle" />Ask anonymously</label>
                ) : <span />}
                <Button onClick={submitAsk} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="hub-ask-submit"><Send className="mr-1.5 h-4 w-4" />Post</Button>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center gap-2 text-sm text-muted-foreground"><MessageSquare className="h-4 w-4" /><span className="tabular-nums">{sortedQuestions.length} questions · sorted by votes</span></div>

          {sortedQuestions.length === 0 ? (
            <EmptyState icon={Sparkles} title="No questions yet" description="Be the first to ask \u2014 the most upvoted questions rise to the top." />
          ) : (
            <div className="space-y-3">
              <AnimatePresence>
                {sortedQuestions.map((q) => (
                  <QuestionCard key={q.question_id} q={q} isCore={isCore} isSpotlight={spotlight?.question_id === q.question_id}
                    onUpvote={toggleUpvote} onSpotlight={doSpotlight} onAnswer={doAnswer} onDismiss={doDismiss} onDelete={doDelete} />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Polls */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><BarChart3 className="h-4 w-4" />Live polls</div>
          {polls.length === 0 ? (
            <Card className="border-dashed bg-card"><CardContent className="p-6 text-center text-sm text-muted-foreground">No polls yet.{isCore ? " Create one to engage the audience." : ""}</CardContent></Card>
          ) : (
            polls.map((p) => <PollCard key={p.poll_id} poll={p} isCore={isCore} onVote={votePoll} onClose={closePoll} onDelete={deletePoll} />)
          )}
        </div>
      </div>

      {/* Reaction bar */}
      <div className="sticky bottom-4 z-20 mx-auto flex w-fit items-center gap-1 rounded-full border border-border bg-card px-2 py-1.5 shadow-[0_12px_30px_rgba(20,20,20,0.12)]">
        {REACTIONS.map((e) => (
          <button key={e} onClick={() => react(e)} className="rounded-full px-2 py-1 text-xl transition-transform hover:scale-125" data-testid="hub-reaction-button">{e}</button>
        ))}
        <AnimatePresence>
          {pointsFlash && (
            <motion.span
              key={pointsFlash.id}
              initial={{ opacity: 0, y: 6, scale: 0.8 }} animate={{ opacity: 1, y: -6, scale: 1 }} exit={{ opacity: 0 }}
              className="ml-1 select-none rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary"
              data-testid="hub-points-flash"
            >+{pointsFlash.pts}</motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Floating reactions */}
      <div className="pointer-events-none fixed inset-0 z-30 overflow-hidden">
        <AnimatePresence>
          {reactions.map((r) => (
            <motion.div key={r.id} initial={{ opacity: 0, y: 0, scale: 0.6 }} animate={{ opacity: 1, y: -220, scale: 1.3 }} exit={{ opacity: 0 }} transition={{ duration: 2.2, ease: "easeOut" }}
              className="absolute bottom-16 text-3xl" style={{ left: `${r.left}%` }}>{r.emoji}</motion.div>
          ))}
        </AnimatePresence>
      </div>

      <CreatePollDialog open={pollOpen} onOpenChange={setPollOpen} sessionId={sessionId} />
      <CheckinDialog open={checkinOpen} onOpenChange={setCheckinOpen} sessionId={sessionId} />
      <LinkLiveDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        sessionId={sessionId}
        live={live}
        onLinked={setLive}
        onUnlinked={() => setLive(null)}
      />
    </div>
  );
}

function CreatePollDialog({ open, onOpenChange, sessionId }) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim() || opts.length < 2) { toast.error("Add a question and at least 2 options"); return; }
    setSaving(true);
    try { await api.post(`/hub/sessions/${sessionId}/polls`, { question: question.trim(), options: opts }); toast.success("Poll launched"); onOpenChange(false); setQuestion(""); setOptions(["", ""]); }
    catch (e) { toast.error("Could not create poll"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-xl">New poll</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Question</Label><Input className="mt-1.5" value={question} onChange={(e) => setQuestion(e.target.value)} data-testid="hub-poll-question-input" /></div>
          <Label>Options</Label>
          {options.map((o, i) => (
            <div key={i} className="flex gap-2">
              <Input value={o} onChange={(e) => setOptions(options.map((x, j) => j === i ? e.target.value : x))} placeholder={`Option ${i + 1}`} data-testid="hub-poll-option-input" />
              {options.length > 2 && <Button variant="ghost" size="icon" onClick={() => setOptions(options.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}
            </div>
          ))}
          {options.length < 6 && <Button variant="secondary" size="sm" onClick={() => setOptions([...options, ""])}><Plus className="mr-1.5 h-4 w-4" />Add option</Button>}
        </div>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={create} disabled={saving} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="hub-poll-create-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Launch poll"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CheckinDialog({ open, onOpenChange, sessionId }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!open) return;
    try { const r = await api.get(`/hub/sessions/${sessionId}/checkins`); setData(r.data); } catch (e) { setData({ checkins: [], rsvps: [] }); }
  }, [open, sessionId]);
  useEffect(() => { load(); }, [load]);

  const checkIn = async (uid) => {
    try { await api.post(`/hub/sessions/${sessionId}/checkin/${uid}`); toast.success("Checked in"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-xl">Check-in roster</DialogTitle></DialogHeader>
        {!data ? <SectionLoader /> : (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-foreground">Checked in ({data.checkins.length})</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {data.checkins.map((c) => <Badge key={c.checkin_id} variant="secondary">{c.name}</Badge>)}
                {data.checkins.length === 0 && <p className="text-sm text-muted-foreground">Nobody checked in yet.</p>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LinkLiveDialog({ open, onOpenChange, sessionId, live, onLinked, onUnlinked }) {
  const [inputs, setInputs] = useState([]);
  const [loadingInputs, setLoadingInputs] = useState(false);
  const [linking, setLinking] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const loadInputs = useCallback(async () => {
    if (!open || live) return;
    setLoadingInputs(true);
    try { const r = await api.get("/stream/live"); setInputs(r.data || []); }
    catch (e) { setInputs([]); }
    finally { setLoadingInputs(false); }
  }, [open, live]);
  useEffect(() => { loadInputs(); }, [loadInputs]);

  const linkExisting = async (liveId) => {
    setLinking(true);
    try {
      const r = await api.post(`/hub/sessions/${sessionId}/live`, { live_id: liveId });
      onLinked(r.data);
      toast.success("Live video linked");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not link live input"); }
    finally { setLinking(false); }
  };

  const createNew = async () => {
    setLinking(true);
    try {
      const r = await api.post(`/hub/sessions/${sessionId}/live`, {});
      onLinked(r.data);
      toast.success("New live input created & linked");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create live input"); }
    finally { setLinking(false); }
  };

  const unlink = async () => {
    setLinking(true);
    try {
      await api.delete(`/hub/sessions/${sessionId}/live`);
      onUnlinked();
      toast.success("Live video unlinked");
    } catch (e) { toast.error("Could not unlink"); }
    finally { setLinking(false); }
  };

  const copy = (val, label) => {
    try { navigator.clipboard.writeText(val); toast.success(`${label} copied`); }
    catch (e) { toast.error("Copy failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{live ? "Live video" : "Link live video"}</DialogTitle>
          <DialogDescription>
            {live ? "Stream to this input from OBS or a hardware encoder — members watch right inside the hub. (For the full studio with guests, cameras and cloud recording, run a show from the Producer Console.)" : "Link a Cloudflare Stream live input for OBS / RTMPS. For camera switching, guests and cloud recording use the Producer Console instead."}
          </DialogDescription>
        </DialogHeader>

        {live ? (
          <div className="space-y-4">
            {/* Advanced — OBS / RTMPS */}
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="flex w-full items-center justify-between" data-testid="hub-advanced-obs-toggle">
                  <span className="text-sm font-medium">Stream with OBS / RTMPS</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-3">
                <div className="rounded-lg border border-border bg-secondary/40 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground"><Video className="h-4 w-4 text-primary" />{live.name || "Live input"}</div>
                  <p className="mt-1 text-xs text-muted-foreground">Paste these in OBS → Settings → Stream (Custom).</p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">RTMPS URL</Label>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={live.rtmps_url || ""} className="font-mono text-xs" data-testid="hub-rtmps-url" />
                    <Button size="icon" variant="secondary" className="shrink-0" onClick={() => copy(live.rtmps_url, "RTMPS URL")} aria-label="Copy RTMPS URL"><Copy className="h-4 w-4" /></Button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Stream key</Label>
                  <div className="flex items-center gap-2">
                    <Input readOnly type={showKey ? "text" : "password"} value={live.rtmps_key || ""} className="font-mono text-xs" data-testid="hub-rtmps-key" />
                    <Button size="icon" variant="secondary" className="shrink-0" onClick={() => setShowKey((v) => !v)} aria-label="Toggle stream key visibility">{showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                    <Button size="icon" variant="secondary" className="shrink-0" onClick={() => copy(live.rtmps_key, "Stream key")} aria-label="Copy stream key"><Copy className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Keep this secret. Anyone with the key can broadcast to your session.</p>
                </div>

                <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm text-muted-foreground">
                  <p className="mb-1 font-medium text-foreground">Quick start (OBS)</p>
                  <ol className="list-decimal space-y-0.5 pl-4">
                    <li>Settings → Stream → Service: <span className="text-foreground">Custom</span></li>
                    <li>Server: paste the <span className="text-foreground">RTMPS URL</span></li>
                    <li>Stream Key: paste the <span className="text-foreground">Stream key</span></li>
                    <li>Add your camera as a <span className="text-foreground">Video Capture Device</span> (USB cameras like the Meeting Owl appear here), then <span className="text-foreground">Start Streaming</span>.</li>
                  </ol>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" className="text-destructive hover:text-destructive" onClick={unlink} disabled={linking} data-testid="hub-unlink-live"><Unlink className="mr-1.5 h-4 w-4" />Unlink</Button>
              <Button onClick={() => onOpenChange(false)} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <Button onClick={() => createNew()} disabled={linking} className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="hub-create-live">
              <Plus className="mr-1.5 h-4 w-4" />Create input for OBS / RTMPS
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center"><span className="bg-background px-2 text-xs uppercase tracking-wider text-muted-foreground">or reuse existing</span></div>
            </div>

            {loadingInputs ? (
              <SectionLoader />
            ) : inputs.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">No existing live inputs yet. Create one above.</p>
            ) : (
              <div className="space-y-2">
                {inputs.map((li) => (
                  <div key={li.live_id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-2.5" data-testid="hub-existing-live">
                    <div className="flex min-w-0 items-center gap-2">
                      <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">{li.name}</p>
                        <p className="text-xs text-muted-foreground">{li.status === "live" ? "On air" : "Idle"}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="secondary" onClick={() => linkExisting(li.live_id)} disabled={linking} data-testid="hub-link-existing">Link</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
